/**
 * R1/#477 incident-as-aggregator — the LOCKED marker-treatment chokepoint.
 *
 * The hotspot/active-fire marker IS the incident's only map presence; there is NO
 * separate incident layer. This module maps an incident's `lifecycle_state`
 * (Ontario IMS: detected → confirmed → owned → response → deactivated) to a marker
 * TREATMENT, which HighRiskHotspotsLayer + ActiveFiresLayer consume. The four LIVE
 * states are distinguishable by STRUCTURE ALONE (glyph presence · keyline · ring +
 * quiet) — hue (urgency) and size (Response Level) stay RESERVED, so the transform
 * never collides with the colour law and survives a deuteranope read:
 *
 *   detected  → dot + white PLATE (no kind-glyph — distinct from a confirmed fire,
 *               and a bare dot would collide with raw 7px hotspots)
 *   confirmed → plate + hazard KIND-GLYPH (a confirmed fire reads as the fire mark)
 *   owned       → + STRUCTURAL keyline (doubled chip backing — IC established, §4.1/§4.5)
 *   response    → + ACK-STEEL managed ring + the urgent pulse QUIETS (active "Response", App A #68)
 *   deactivated → REMOVED from the live layer (demobilization → deactivation, §8); the row
 *               persists + the incident stays retrievable via the AAR replay
 *
 * The state→structure map is PINNED by incidentLifecycleStates.lock.test.ts (mirrors
 * the defaultViewLock pattern): changing it = founder authorization.
 */
import type { HotspotLevelStyle } from './responseLevel';
import { ACK_STEEL, ACK_STEEL_ALPHA } from '../layers/anomalyMarker';
import { INCIDENT_LIFECYCLE_STATES, type IncidentLifecycleState } from '@tsr/shared-types';

export { INCIDENT_LIFECYCLE_STATES };
export type { IncidentLifecycleState };

/** The structural channels a lifecycle state drives on the existing marker. NO hue
 *  channel — hue is RESERVED for urgency (the dot stays red, or ACK-STEEL when quiet). */
export interface LifecycleMarkerStructure {
  /** Render the hazard kind-glyph inside the chip plate. false ⇒ plate only ('detected'). */
  showKindGlyph: boolean;
  /** Draw the structural keyline (doubled chip backing) — 'owned' + 'response'. */
  keyline: boolean;
  /** Draw the ACK-STEEL managed ring (re-sourced from the old owned_incident gate) — 'response'. */
  managedRing: boolean;
  /** Quiet the marker — route the dot/glow through the ACK-STEEL-static path. */
  quiet: boolean;
  /** Drop from the live layer (ClearedGhostTracker ghosts it ~60s, then auto-removes). */
  removeFromLive: boolean;
}

/**
 * The LOCKED state → structure map. Each forward step ADDS a structural cue, so the
 * four live states are separable without colour or motion alone. (Pinned by the lock test.)
 */
export const INCIDENT_LIFECYCLE_MARKER: Record<IncidentLifecycleState, LifecycleMarkerStructure> = {
  detected: { showKindGlyph: false, keyline: false, managedRing: false, quiet: false, removeFromLive: false },
  confirmed: { showKindGlyph: true, keyline: false, managedRing: false, quiet: false, removeFromLive: false },
  owned: { showKindGlyph: true, keyline: true, managedRing: false, quiet: false, removeFromLive: false },
  response: { showKindGlyph: true, keyline: true, managedRing: true, quiet: true, removeFromLive: false },
  deactivated: { showKindGlyph: true, keyline: true, managedRing: false, quiet: true, removeFromLive: true },
};

/** The full marker treatment a layer applies for one feature. */
export interface IncidentLifecycleMarkerTreatment extends LifecycleMarkerStructure {
  /** The dot/glow style after the lifecycle override ('response' ⇒ ACK-STEEL static). */
  style: HotspotLevelStyle;
}

/** The default treatment for a marker NOT owned by a tracked incident — the current
 *  pre-R1 behaviour (plate + kind-glyph, urgency-driven). Identity for lifecycle_state=null. */
const DEFAULT_STRUCTURE: LifecycleMarkerStructure = {
  showKindGlyph: true,
  keyline: false,
  managedRing: false,
  quiet: false,
  removeFromLive: false,
};

/** Quiet a base level-style to the ACK-STEEL static read (the 'response' active-response
 *  cue) — the same recolour/destatic the Law-3 acknowledgedStyle applies, reusing the
 *  existing ACK-STEEL tokens (no new hue). Kept local so this stays a leaf style module. */
function quietStyle(base: HotspotLevelStyle): HotspotLevelStyle {
  return {
    ...base,
    color: ACK_STEEL,
    alpha: ACK_STEEL_ALPHA,
    pulse: false,
    pulseAmplitude: 0,
    ring: false,
    hideAtCountry: true,
  };
}

/**
 * Resolve a marker's incident-lifecycle treatment. `base` is the already
 * ack-resolved level style (so an acknowledged anomaly that is ALSO a tracked
 * incident still reads correctly). A null/undefined lifecycle_state ⇒ the default
 * (non-incident) marker. The single seam both dot layers route the lifecycle through.
 */
export function incidentLifecycleTreatment(
  base: HotspotLevelStyle,
  lifecycleState: IncidentLifecycleState | null | undefined,
): IncidentLifecycleMarkerTreatment {
  const s = lifecycleState ? INCIDENT_LIFECYCLE_MARKER[lifecycleState] : DEFAULT_STRUCTURE;
  return { ...s, style: s.quiet ? quietStyle(base) : base };
}
